import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, InboxIcon } from 'lucide-react';
import { SnapshotRow } from '../types/borrowerChecker';
interface MissingRecordsTableProps {
  records: SnapshotRow[];
}
const INITIAL_DISPLAY_COUNT = 10;
export function MissingRecordsTable({
  records
}: MissingRecordsTableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const displayedRecords = showAll ? records : records.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = records.length > INITIAL_DISPLAY_COUNT;
  if (records.length === 0) {
    return <section aria-label="Missing records" data-id="element-64">
        <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between bg-white rounded-lg border border-gray-200 shadow-sm p-5 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300" aria-expanded={isExpanded} data-id="element-65">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-66">
            Preview Missing Records
          </h3>
          {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" aria-hidden="true" data-id="element-67" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" data-id="element-68" />}
        </button>
        {isExpanded && <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg p-10" data-id="element-69">
            <div className="flex flex-col items-center gap-3 text-center" data-id="element-70">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center" data-id="element-71">
                <InboxIcon className="w-7 h-7 text-green-500" aria-hidden="true" data-id="element-72" />
              </div>
              <p className="text-sm font-medium text-gray-700" data-id="element-73">
                No missing records found
              </p>
              <p className="text-xs text-gray-500" data-id="element-74">
                All snapshot records matched internal records.
              </p>
            </div>
          </div>}
      </section>;
  }
  return <section aria-label="Missing records" data-id="element-75">
      <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between bg-white rounded-lg border border-gray-200 shadow-sm p-5 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300" aria-expanded={isExpanded} data-id="element-76">
        <div className="flex items-center gap-3" data-id="element-77">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-78">
            Preview Missing Records
          </h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" data-id="element-79">
            {records.length} record{records.length !== 1 ? 's' : ''}
          </span>
        </div>
        {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" aria-hidden="true" data-id="element-80" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" data-id="element-81" />}
      </button>

      {isExpanded && <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg overflow-hidden" data-id="element-82">
          <div className="overflow-x-auto" data-id="element-83">
            <table className="w-full text-sm" role="table" data-id="element-84">
              <thead data-id="element-85">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-86">
                  <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-87">
                    Row #
                  </th>
                  <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-88">
                    Lender Loan ID
                  </th>
                  <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-89">
                    Borrower Name
                  </th>
                  <th scope="col" className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-90">
                    Loan Amount
                  </th>
                  <th scope="col" className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-91">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-92">
                {displayedRecords.map((record, index) => <tr key={`${record.lenderLoanId}-${record.rowNumber}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-93">
                    <td className="px-5 py-3 text-gray-500 font-mono text-xs" data-id="element-94">
                      {record.rowNumber}
                    </td>
                    <td className="px-5 py-3 text-gray-900 font-medium" data-id="element-95">
                      {record.lenderLoanId}
                    </td>
                    <td className="px-5 py-3 text-gray-700" data-id="element-96">
                      {record.borrowerName || '—'}
                    </td>
                    <td className="px-5 py-3 text-gray-700 text-right font-mono" data-id="element-97">
                      {record.loanAmount ? `$${Number(record.loanAmount).toLocaleString()}` : '—'}
                    </td>
                    <td className="px-5 py-3 text-center" data-id="element-98">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700" data-id="element-99">
                        Missing
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {hasMore && !showAll && <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50" data-id="element-100">
              <button type="button" onClick={() => setShowAll(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium focus:outline-none focus:underline" data-id="element-101">
                Show all {records.length} records
              </button>
            </div>}

          {showAll && hasMore && <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50" data-id="element-102">
              <button type="button" onClick={() => setShowAll(false)} className="text-sm text-blue-600 hover:text-blue-700 font-medium focus:outline-none focus:underline" data-id="element-103">
                Show fewer records
              </button>
            </div>}
        </div>}
    </section>;
}